import {
    Fragment,
    FunctionComponent,
    useCallback,
    useMemo,
    useState,
} from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { FormDrawer } from '@starjack/shared-components';
import { LoadingContainer } from '@starjack/shared-components';
import {
    useGetConfigurationsQuery,
    usePutWebshopMutation,
} from '@/api/v2/configurations';
import { useGetAllLiftOperatorsQuery } from '@/api/v2/liftOperator';
import { createNewWebshop, Webshop } from '@/models/webshop';
import { WebshopDrawerContent } from '@/pageContents/webshopsContent/WebshopDrawerContent';
import { WebshopDeleteDialog } from '@/pageContents/webshopsContent/WebshopDeleteDialog';
import { WebshopFilter } from '@/pageContents/webshopsContent/WebshopFilter';
import { WebshopRow } from '@/pageContents/webshopsContent/WebshopRow';
import { debounce } from 'lodash';
import { useNotification } from '@starjack/features/notifications';

export const WebshopsPageContent: FunctionComponent = () => {
    const theme = useTheme();
    const { data: webshops, isLoading: isLoadingWebshops } =
        useGetConfigurationsQuery();
    const { data: liftOperators, isLoading: isLoadingLiftOperators } =
        useGetAllLiftOperatorsQuery();
    const [putWebshop] = usePutWebshopMutation();
    const { addNotification } = useNotification();
    const [drawerConfig, setDrawerConfig] = useState<{
        isOpen: boolean;
        webshop: Webshop;
    }>({ isOpen: false, webshop: null });
    const [deleteDialogConfig, setDeleteDialogConfig] = useState<{
        isOpen: boolean;
        webshop: Webshop;
    }>({ isOpen: false, webshop: null });
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [showInactive, setShowInactive] = useState<boolean>(false);
    const [loadingWebshopId, setLoadingWebshopId] = useState<string | null>(
        null
    );

    const onSearchChange = useCallback(
        debounce((value) => {
            setSearchTerm(value);
        }, 250),
        []
    );

    const onShowInactiveChange = useCallback((showInactive: boolean) => {
        setShowInactive(showInactive);
    }, []);

    const getLiftOperatorName = useCallback(
        (liftOperatorId: string) => {
            if (!liftOperators) return '';
            const liftOperator = liftOperators.find(
                (lo) => lo.id === liftOperatorId
            );
            return liftOperator ? liftOperator.name?.de : '';
        },
        [liftOperators]
    );

    const onToggleActiveStatus = useCallback(
        (webshop: Webshop) => {
            if (loadingWebshopId) return; // Prevent multiple simultaneous requests

            setLoadingWebshopId(webshop.id);

            const updatedWebshop = {
                ...webshop,
                active: !webshop.active,
            };

            putWebshop(updatedWebshop)
                .unwrap()
                .then(() => {
                    addNotification({
                        message: `Webshop ${
                            updatedWebshop.active ? 'aktiviert' : 'deaktiviert'
                        }`,
                        type: 'success',
                    });
                })
                .catch((reason) => {
                    console.error(reason);
                    addNotification({
                        message:
                            'Fehler beim Aktualisieren des Webshop-Status. Bitte später noch einmal versuchen',
                        type: 'error',
                    });
                })
                .finally(() => {
                    setLoadingWebshopId(null);
                });
        },
        [putWebshop, addNotification, loadingWebshopId]
    );

    const onCloseDrawer = () => {
        setDrawerConfig((prevState) => ({
            ...prevState,
            isOpen: false,
        }));
    };

    const filteredWebshops = useMemo(() => {
        if (!webshops) {
            return [];
        }

        return webshops
            .filter((webshop) => {
                // Filter by search term (only if search term has more than 3 characters)
                const matchesSearch =
                    searchTerm.length <= 3 ||
                    webshop.vanityIdentifier.includes(searchTerm);
                // Filter by active status
                const matchesActiveFilter = showInactive
                    ? !webshop.active
                    : webshop.active;

                return matchesSearch && matchesActiveFilter;
            })
            .sort((a, b) =>
                a.vanityIdentifier.localeCompare(b.vanityIdentifier)
            );
    }, [webshops, searchTerm, showInactive]);

    const dataLoading = isLoadingWebshops || isLoadingLiftOperators;

    return (
        <Container
            sx={{
                mt: 3,
                ml: 'unset',
            }}
            maxWidth='lg'
        >
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Webshopverwaltung
            </Typography>
            <Stack spacing={theme.page.verticalSpacing}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        variant='contained'
                        onClick={() => {
                            setDrawerConfig({
                                isOpen: true,
                                webshop: createNewWebshop(),
                            });
                        }}
                    >
                        + Neuen Webshop anlegen
                    </Button>
                </Box>
                <WebshopFilter
                    onChange={onSearchChange}
                    onShowInactiveChange={onShowInactiveChange}
                />
                <Card>
                    <CardContent
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant='h6'>Angelegte Webshops</Typography>
                    </CardContent>
                    <Divider
                        sx={{
                            width: '100%',
                        }}
                    />

                    {dataLoading ? (
                        <CardContent
                            sx={{
                                position: 'relative',
                                minHeight: '120px',
                            }}
                        >
                            <LoadingContainer cover />
                        </CardContent>
                    ) : (
                        filteredWebshops?.map((webshop, index) => (
                            <Fragment key={index}>
                                <WebshopRow
                                    webshop={webshop}
                                    liftOperatorName={getLiftOperatorName(
                                        webshop.liftOperatorId
                                    )}
                                    isLoading={loadingWebshopId === webshop.id}
                                    onToggleActiveStatus={onToggleActiveStatus}
                                    onEdit={(webshop) => {
                                        setDrawerConfig({
                                            isOpen: true,
                                            webshop,
                                        });
                                    }}
                                    onDelete={(webshop) => {
                                        setDeleteDialogConfig({
                                            isOpen: true,
                                            webshop,
                                        });
                                    }}
                                />

                                {index < filteredWebshops.length - 1 && (
                                    <Divider
                                        sx={{
                                            width: '100%',
                                        }}
                                    />
                                )}
                            </Fragment>
                        ))
                    )}
                </Card>

                <FormDrawer
                    anchor='right'
                    open={drawerConfig.isOpen}
                    onClose={onCloseDrawer}
                >
                    <WebshopDrawerContent
                        webshop={drawerConfig.webshop}
                        onClose={onCloseDrawer}
                    />
                </FormDrawer>

                <WebshopDeleteDialog
                    webshop={deleteDialogConfig.webshop}
                    open={deleteDialogConfig.isOpen}
                    onClose={() => {
                        setDeleteDialogConfig((prevState) => ({
                            ...prevState,
                            isOpen: false,
                        }));
                    }}
                />
            </Stack>
        </Container>
    );
};
